import { useActionState, startTransition } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { emailSchema } from "@src/validation/schemas";
import { Input } from "@src/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@src/components/ui/form";
import { signInAction, type ActionState, type SignInInput } from "../../actions";
import { paths } from "@src/utils";
import { EXAMPLE_EMAIL } from "@src/constants";
import Link from "next/link";
import { AuthButton, FormAlert, AuthCard } from "../../_components";
import { PasswordInput } from "../../../_components/PasswordInput";
import { Button } from "@src/components/ui/button";
const passwordFormSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, {
    message: "Password is required."
  })
});
type PasswordSignInFormProps = {
  redirectPath?: string;
  onSwitchToMagicLink: () => void;
};
export function PasswordSignInForm({
  redirectPath,
  onSwitchToMagicLink
}: PasswordSignInFormProps) {
  const [state, formAction, isPending] = useActionState<ActionState, SignInInput>(signInAction, {
    result: "idle"
  });
  const form = useForm<z.infer<typeof passwordFormSchema>>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: {
      email: "",
      password: ""
    }
  });
  function onSubmit(values: z.infer<typeof passwordFormSchema>) {
    startTransition(() => {
      formAction({
        email: values.email,
        password: values.password,
        redirect: redirectPath
      });
    });
  }
  return <AuthCard title="Sign in to your account" description="Enter your email below to login to your account" footer={<div className="text-sm text-muted-foreground">
          Don&apos;t have an account?{" "}
          <Link href={paths.register} className="text-primary font-medium hover:underline">
            Register
          </Link>
        </div>} data-sentry-element="AuthCard" data-sentry-component="PasswordSignInForm" data-sentry-source-file="PasswordSignInForm.tsx">
      <Form {...form} data-sentry-element="Form" data-sentry-source-file="PasswordSignInForm.tsx">
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" noValidate>
          <FormField control={form.control} name="email" render={({
          field
        }) => <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input type="email" placeholder={EXAMPLE_EMAIL} {...field} ref={undefined} />
                </FormControl>
                <FormMessage />
              </FormItem>} data-sentry-element="FormField" data-sentry-source-file="PasswordSignInForm.tsx" />

          <FormField control={form.control} name="password" render={({
          field
        }) => <FormItem>
                <div className="flex items-center">
                  <FormLabel>Password</FormLabel>
                  <Link href={paths.recover} className="ml-auto inline-block text-sm underline-offset-4 hover:underline" data-ph-capture-attribute-action="auth-forgot-password">
                    Forgot password?
                  </Link>
                </div>
                <FormControl>
                  <PasswordInput {...field} ref={undefined} />
                </FormControl>
                <FormMessage />
              </FormItem>} data-sentry-element="FormField" data-sentry-source-file="PasswordSignInForm.tsx" />

          <AuthButton isPending={isPending} text="Sign in" pendingText="Signing in..." data-ph-capture-attribute-action="auth-signin-password-submit" data-sentry-element="AuthButton" data-sentry-source-file="PasswordSignInForm.tsx" />

          <FormAlert state={state} isPending={isPending} data-sentry-element="FormAlert" data-sentry-source-file="PasswordSignInForm.tsx" />

          <div className="text-center">
            <Button type="button" variant="link" onClick={onSwitchToMagicLink} className="text-sm" data-ph-capture-attribute-action="auth-switch-to-magic-link" data-sentry-element="Button" data-sentry-source-file="PasswordSignInForm.tsx">
              Sign in with magic link
            </Button>
          </div>
        </form>
      </Form>
    </AuthCard>;
}